import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { authService } from '../../services/api';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';
import logoGM from '../../assets/logo_gm.png';

const PasswordResetConfirm = () => {
  const { t } = useTranslation();
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [backendErrors, setBackendErrors] = useState([]);
  const [success, setSuccess] = useState(false);

  // Validações de senha
  const passwordValidation = {
    minLength: password1.length >= 9,
    hasUpperCase: /[A-Z]/.test(password1),
    hasLowerCase: /[a-z]/.test(password1),
    hasNumber: /[0-9]/.test(password1),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password1),
  };

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);

  const ValidationItem = ({ valid, text }) => (
    <ListItem dense sx={{ py: 0.5 }}>
      <ListItemIcon sx={{ minWidth: 36 }}>
        {valid ? (
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
        ) : (
          <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />
        )}
      </ListItemIcon>
      <ListItemText
        primary={text}
        primaryTypographyProps={{
          variant: 'body2',
          sx: {
            color: valid ? 'success.main' : 'text.secondary',
          },
        }}
      />
    </ListItem>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBackendErrors([]);
    setSuccess(false);

    if (!password1 || !password2) {
      setError(t('passwordResetConfirm.fillAllFields'));
      return;
    }

    if (password1 !== password2) {
      setError(t('passwordResetConfirm.passwordMismatch'));
      return;
    }

    if (!isPasswordValid) {
      setError(t('passwordResetConfirm.passwordRequirements'));
      return;
    }

    setLoading(true);

    try {
      await authService.passwordResetConfirm(uid, token, password1, password2);
      setSuccess(true);

      // Redirects to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Password reset confirm error:', err);

      // Check if backend returned validation errors
      if (err.response?.data) {
        const errors = [];
        const data = err.response.data;

        // Handle new_password2 errors (most common)
        if (data.new_password2) {
          errors.push(...(Array.isArray(data.new_password2) ? data.new_password2 : [data.new_password2]));
        }

        // Handle new_password1 errors
        if (data.new_password1) {
          errors.push(...(Array.isArray(data.new_password1) ? data.new_password1 : [data.new_password1]));
        }

        // Handle token errors
        if (data.token) {
          errors.push(...(Array.isArray(data.token) ? data.token : [data.token]));
        }

        // Handle uid errors
        if (data.uid) {
          errors.push(...(Array.isArray(data.uid) ? data.uid : [data.uid]));
        }

        // Handle non_field_errors
        if (data.non_field_errors) {
          errors.push(...(Array.isArray(data.non_field_errors) ? data.non_field_errors : [data.non_field_errors]));
        }

        if (errors.length > 0) {
          setBackendErrors(errors);
        } else if (err.response?.status === 400) {
          setError(t('passwordResetConfirm.invalidToken'));
        } else {
          setError(t('passwordResetConfirm.error'));
        }
      } else if (err.response?.status === 400) {
        setError(t('passwordResetConfirm.invalidToken'));
      } else {
        setError(t('passwordResetConfirm.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.paper',
        position: 'relative',
      }}
    >
      {/* Language Selector - Top Right */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
        }}
      >
        <LanguageSelector />
      </Box>

      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'background.default',
            borderRadius: 2,
          }}
        >
          <Box
            component="img"
            src={logoGM}
            alt="GM Logo"
            sx={{
              width: 120,
              height: 120,
              mb: 2,
              objectFit: 'contain',
            }}
          />

          <Typography
            component="h1"
            variant="h5"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {t('passwordResetConfirm.title')}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mb: 3,
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            {t('passwordResetConfirm.subtitle')}
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ width: '100%' }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {backendErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                  {backendErrors.map((err, index) => (
                    <li key={index}>{err}</li>
                  ))}
                </Box>
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('passwordResetConfirm.success')}
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              name="password1"
              label={t('passwordResetConfirm.newPassword')}
              type={showPassword1 ? 'text' : 'password'}
              id="password1"
              autoComplete="new-password"
              value={password1}
              onChange={(e) => setPassword1(e.target.value)}
              disabled={loading || success}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword1(!showPassword1)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      disabled={loading || success}
                    >
                      {showPassword1 ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password2"
              label={t('passwordResetConfirm.confirmPassword')}
              type={showPassword2 ? 'text' : 'password'}
              id="password2"
              autoComplete="new-password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              disabled={loading || success}
              error={password2.length > 0 && password1 !== password2}
              helperText={
                password2.length > 0 && password1 !== password2
                  ? t('passwordResetConfirm.passwordMismatch')
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword2(!showPassword2)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      disabled={loading || success}
                    >
                      {showPassword2 ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {/* Password Requirements */}
            {password1.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: 'text.secondary',
                  }}
                >
                  {t('passwordResetConfirm.passwordRequirements')}
                </Typography>
                <List sx={{ py: 0 }}>
                  <ValidationItem
                    valid={passwordValidation.minLength}
                    text={t('passwordResetConfirm.minLength')}
                  />
                  <ValidationItem
                    valid={passwordValidation.hasUpperCase}
                    text={t('passwordResetConfirm.hasUpperCase')}
                  />
                  <ValidationItem
                    valid={passwordValidation.hasLowerCase}
                    text={t('passwordResetConfirm.hasLowerCase')}
                  />
                  <ValidationItem
                    valid={passwordValidation.hasNumber}
                    text={t('passwordResetConfirm.hasNumber')}
                  />
                  <ValidationItem
                    valid={passwordValidation.hasSpecialChar}
                    text={t('passwordResetConfirm.hasSpecialChar')}
                  />
                </List>
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || success}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('passwordResetConfirm.resetButton')
              )}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PasswordResetConfirm;
